import type { KeyboardEventHandler, ReactNode } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { FormatOptionLabelMeta, GroupBase, StylesConfig } from 'react-select';
import SelectAsync from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { HcpSearchResult, KolSearchForm, KolSearchResult } from '@trinity/chat';
import { KolSearchStatusEnum } from '@trinity/chat';
import { useSelectedUserTerritoryId } from '@trinity/chat-components/components/Chat.Filters/hooks/useSelectedUserTerritoryId';
import {
  buildSelectStyles,
  buildSelectTheme,
} from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useHasSmartRouting } from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import {
  DispatchKolSearchFormContext,
  KolSearchStatusContext,
  KolSearchSubmitContext,
  KolSearchFormContext,
} from './context';
import { BasicSystemUserMessage } from './Message.System.Basic';

type KolSearchFormMessageProps = {
  disabled?: boolean;
  onAutocompleteSelect?: (kol: KolSearchResult) => void;
  onLocationSelect?: (location: KolSearchForm['location']) => void;
  showSearchButton?: boolean;
};

export const KolSearchFormMessage = ({
  disabled = false,
  onAutocompleteSelect,
  onLocationSelect,
  showSearchButton = true,
}: KolSearchFormMessageProps) => {
  const searchStatus = useContext(KolSearchStatusContext);
  const handleSubmit = useContext(KolSearchSubmitContext);
  const formState = useContext(KolSearchFormContext);
  const setFormState = useContext(DispatchKolSearchFormContext);
  const inputFocused = useRef(false);

  const copy = `Let's get started. First lets find the HCP you'd like to profile!`;
  const instructions = `Enter details to find the HCP you would like to profile.`;

  const submitDisabled = useMemo(() => {
    return (
      disabled || (!formState.name && !formState.location) || searchStatus !== KolSearchStatusEnum.FormEntry
    );
  }, [disabled, formState.location, formState.name, searchStatus]);

  const inputDisabled = useMemo(() => {
    return disabled || searchStatus !== KolSearchStatusEnum.FormEntry;
  }, [disabled, searchStatus]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!inputFocused.current) return;

      if (submitDisabled) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [submitDisabled, handleSubmit]
  );

  const handleInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const onKolSelect = useCallback(
    (kol: KolSearchResult) => {
      if (disabled) {
        return;
      }

      if (onAutocompleteSelect) {
        onAutocompleteSelect(kol);
        return;
      }

      setFormState({ kolId: kol.id, name: kol.name });

      // Submit after state is committed so handlers read the selected HCP.
      setTimeout(() => {
        handleSubmit();
      }, 0);
    },
    [disabled, handleSubmit, onAutocompleteSelect, setFormState]
  );

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          {copy}
          <Divider />
          <Instructions>{instructions}</Instructions>

          <KolSearchInputForm
            formState={formState}
            inputDisabled={inputDisabled}
            onKeyDown={handleKeyDown}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
            onKolSelect={onKolSelect}
            setFormState={setFormState}
            onLocationSelect={onLocationSelect}
          />
          {showSearchButton ? (
            <>
              <Divider />
              <SearchQueryHint
                disabled={submitDisabled}
                onClick={handleSubmit}
                value="Search for profiles."
              />
            </>
          ) : null}
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

// Base type for search results
type BaseSearchResult = {
  name?: string;
} & ({ id?: number } | { hcpIdentifier?: string });

type HcpSelectProps<T extends BaseSearchResult = KolSearchResult> = {
  disabled?: boolean;
  formatOptionLabel?: (data: T, formatOptionLabelMeta: FormatOptionLabelMeta<T>) => ReactNode;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: (e: string) => void;
  onSelection: (result: T) => void;
  placeholder?: string;
  inputValue?: string;
  isClearable?: boolean;
  value?: T | null;
  queryFn: (params: {
    name: string;
    brandIdentifier: string;
    territoryId?: string;
    lat?: number;
    lon?: number;
  }) => Promise<{ results: T[] }>;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
};

export function SelectHcp<T extends BaseSearchResult = KolSearchResult>({
  disabled,
  formatOptionLabel,
  onKeyDown,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onSelection,
  placeholder = 'Search HCPs',
  inputValue = '',
  isClearable,
  value,
  queryFn,
  getOptionLabel,
  getOptionValue,
}: HcpSelectProps<T>) {
  const brandIdentifier = useActiveBrandIdentifier();
  const territoryId = useSelectedUserTerritoryId();

  const searchQuery = useQuery({
    queryKey: ['get:search', inputValue, brandIdentifier, territoryId],
    queryFn: () => {
      if (!brandIdentifier) {
        return Promise.resolve({ results: [] as T[] });
      }

      return queryFn({ name: inputValue, brandIdentifier, territoryId });
    },
    enabled: inputValue.length > 2 && !disabled && Boolean(brandIdentifier),
    placeholderData: { results: [] },
  });

  const queryResults = searchQuery.data?.results ?? [];

  const theme = useTheme();
  const selectStyles = useMemo(
    () => buildSelectStyles(theme) as StylesConfig<T, false, GroupBase<T>>,
    [theme]
  );
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const defaultGetOptionLabel = (option: T): string => option.name ?? '';

  const defaultGetOptionValue = (option: T): string => {
    if ('id' in option && typeof option.id === 'number') return option.id.toString();
    if ('hcpIdentifier' in option && typeof option.hcpIdentifier === 'string') return option.hcpIdentifier;
    if (option.name) return option.name;
    return '';
  };

  const defaultFormatOptionLabel = (data: T) => {
    if (formatOptionLabel) return formatOptionLabel(data, {} as FormatOptionLabelMeta<T>);

    if ('specialties' in data && 'affiliations' in data) {
      return formatKolOptionLabel(data as unknown as KolSearchResult);
    }

    if ('specialty' in data && 'primaryAffiliationName' in data) {
      return formatHcpOptionLabel(data as unknown as HcpSearchResult);
    }

    // Fallback to just name
    return <div>{data.name}</div>;
  };

  return (
    <SelectAsync<T>
      menuPortalTarget={document.body}
      isDisabled={disabled}
      isClearable={isClearable}
      onKeyDown={queryResults.length ? undefined : onKeyDown}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      onChange={(option) => {
        if (option) {
          onSelection(option);
        }
      }}
      inputValue={inputValue}
      value={value}
      isLoading={searchQuery.isFetching}
      options={queryResults}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      getOptionValue={getOptionValue || defaultGetOptionValue}
      formatOptionLabel={defaultFormatOptionLabel}
      noOptionsMessage={() => null}
      onInputChange={(e, meta) => (meta.action === 'input-change' ? onInputChange(e) : null)}
      placeholder={placeholder}
      styles={selectStyles}
      filterOption={() => true}
      theme={selectTheme}
    />
  );
}

type FormProps = {
  className?: string;
  inputDisabled: boolean;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onKolSelect: (kol: KolSearchResult) => void;
  formState: Partial<KolSearchForm>;
  setFormState: (form: Partial<KolSearchForm>) => void;
  onLocationSelect?: (location: KolSearchForm['location']) => void;
};

function mapHcpResultToKolSearchResult(hcp: HcpSearchResult): KolSearchResult {
  const numericId = Number.parseInt(hcp.hcpIdentifier, 10);
  const normalizedSpecialty = hcp.specialty?.trim();

  return {
    id: Number.isNaN(numericId) ? 0 : numericId,
    isKol: true,
    affiliations: hcp.primaryAffiliationName
      ? [
          {
            name: hcp.primaryAffiliationName,
            city: null,
            state: null,
            street: null,
            country: null,
            role: null,
            zip: null,
          },
        ]
      : [],
    name: hcp.name,
    firstName: hcp.firstName ?? null,
    lastName: hcp.lastName ?? null,
    email: hcp.email ?? null,
    phone: hcp.phone ?? null,
    qualifications: [],
    specialties: normalizedSpecialty ? [normalizedSpecialty] : [],
    city: null,
    state: null,
    streetAddress: null,
    country: null,
    zipCode: null,
    distance: hcp.distance ?? null,
    locationConfidence: null,
    locationCoords: hcp.primaryLocation
      ? {
          latitude: hcp.primaryLocation.lat,
          longitude: hcp.primaryLocation.lon,
        }
      : null,
    locationName: hcp.location ?? null,
    recommendations: null,
  };
}

export const KolSearchInputForm = ({
  className,
  formState,
  inputDisabled,
  onInputBlur,
  onInputFocus,
  onKeyDown,
  onKolSelect,
  setFormState,
  onLocationSelect,
}: FormProps) => {
  const apiClient = useChatApiClient();
  const searchName = formState.name ?? '';
  const locationValue = formState.location ?? {
    id: '',
    name: '',
    latitude: 0,
    longitude: 0,
  };

  const [locationError, setLocationError] = useState<string | null>(null);
  const { getLocation, isSupported, state } = useBrowserLocation({
    onSuccess: (result) => {
      setFormState({
        location: {
          id: 'browser-location',
          latitude: result.latitude,
          longitude: result.longitude,
          name: '',
        },
      });
    },
    onError: setLocationError,
  });

  const hasSmartRouting = useHasSmartRouting();

  const searchHcpOptions = useCallback(
    async ({
      name,
      brandIdentifier: selectBrandIdentifier,
      territoryId,
    }: {
      name: string;
      brandIdentifier: string;
      territoryId?: string;
    }) => {
      const res = await apiClient.search.searchHcpsByName({
        name,
        brandIdentifier: selectBrandIdentifier,
        territoryId,
      });
      return {
        // TODO: Fix this to not use kolId
        results: res.results.map(mapHcpResultToKolSearchResult),
      };
    },
    [apiClient]
  );

  return (
    <div className={className}>
      <FormItem>
        <Label>Search by Name</Label>
        <SelectHcp
          placeholder="Name"
          disabled={inputDisabled}
          onKeyDown={onKeyDown}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
          onInputChange={(name) => setFormState({ ...formState, name })}
          onSelection={(kol) => onKolSelect(kol)}
          queryFn={searchHcpOptions}
          inputValue={searchName}
        />
      </FormItem>
      {hasSmartRouting && (
        <>
          <Label>OR</Label>
          <FormItem>
            <Label>Search by address or current location</Label>
            <SelectLocation
              styles={{
                container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
              }}
              isDisabled={inputDisabled}
              value={locationValue}
              onChange={(location) => {
                setFormState({ ...formState, location });
                if (onLocationSelect) {
                  onLocationSelect(location);
                }
              }}
            />
            {isSupported && (
              <GetLocationSection>
                <GetLocationButton onClick={getLocation}>Share my current location</GetLocationButton>
                {state === 'error' && (
                  <BrowserLocationError
                    errorMessage={locationError ?? 'Unable to determine your location.'}
                  />
                )}
              </GetLocationSection>
            )}
          </FormItem>
        </>
      )}
    </div>
  );
};

function formatKolOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  const primaryAffiliation = result.affiliations?.[0]?.name;
  return (
    <div>
      {result.name}
      {specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {primaryAffiliation ? <KolResultSecondaryText>{primaryAffiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

function formatHcpOptionLabel(result: HcpSearchResult) {
  const specialty = result.specialty?.trim();
  const affiliation =
    result.primaryAffiliationName && result.primaryAffiliationName !== 'N'
      ? result.primaryAffiliationName.trim()
      : null;
  return (
    <div>
      {result.name}
      {specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {affiliation ? <KolResultSecondaryText>{affiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));

const Body = styled.div({
  width: 500,
});

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});

const Divider = styled.div(({ theme }) => ({
  width: '100%',
  height: 2,
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const Instructions = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 20,
}));

const Label = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 10,
}));

const FormItem = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  marginBottom: 10,
});

const GetLocationSection = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 5,
});

const GetLocationButton = styled.div(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  fontSize: 14,
}));
